import { createGroup } from "app/client/components/commands";
import { buildDuplicatePageDialog } from "app/client/components/duplicatePage";
import { GristDoc } from "app/client/components/GristDoc";
import { makeT } from "app/client/lib/localization";
import { getRegionalCapabilitiesObs, isRegionalStructureEditor } from "app/client/lib/RegionalMode";
import { logTelemetryEvent } from "app/client/lib/telemetry";
import { PageRec } from "app/client/models/DocModel";
import { urlState } from "app/client/models/gristUrlState";
import MetaTableModel from "app/client/models/MetaTableModel";
import { find as findInTree, fromTableData, TreeItemRecord, TreeRecord,
  TreeTableData } from "app/client/models/TreeModel";
import { TreeViewComponent } from "app/client/ui/TreeViewComponent";
import { cssRadioCheckboxOptions, radioCheckboxOption } from "app/client/ui2018/checkbox";
import { theme } from "app/client/ui2018/cssVars";
import { cssLink } from "app/client/ui2018/links";
import { ISaveModalOptions, saveModal } from "app/client/ui2018/modals";
import { buildCensoredPage, buildPageDom, PageOptions } from "app/client/ui2018/pages";
import { mod } from "app/common/gutil";

import { Computed, Disposable, dom, fromKo, makeTestId, observable, Observable, styled } from "grainjs";

const t = makeT("Pages");

// build dom for the tree view of pages
export function buildPagesDom(owner: Disposable, activeDoc: GristDoc, isOpen: Observable<boolean>) {
  const pagesTable = activeDoc.docModel.pages;
  const buildDom = buildDomFromTable.bind(null, pagesTable, activeDoc);
  const searchValue = Observable.create(owner, "");

  const records = Computed.create<TreeRecord[]>(owner, use =>
    use(activeDoc.docModel.menuPages).map(page => ({
      pageName: use(use(page.view).name),
      id: page.getRowId(),
      indentation: use(page.indentation),
      pagePos: use(page.pagePos),
      viewRef: use(page.viewRef),
      hidden: use(page.isCensored),
      collapsed: page.isCollapsed,
    })),
  );
  const filteredRecords = Computed.create<TreeRecord[]>(owner, use => {
    const searchText = normalizePageSearchText(use(searchValue));
    const rawRecords = use(records);
    if (!searchText) {
      return rawRecords;
    }
    return filterPageRecords(rawRecords, searchText);
  });
  const hasSearch = Computed.create(owner, use => Boolean(normalizePageSearchText(use(searchValue))));
  const hasResults = Computed.create(owner, use => use(filteredRecords).length > 0);
  const getTreeTableData = (): TreeTableData => ({
    getRecords: () => filteredRecords.get(),
    sendTableActions: (...args) => pagesTable.tableData.sendTableActions(...args),
  });

  // create the model and keep in sync with the table
  const model = observable(fromTableData(getTreeTableData(), buildDom));
  owner.autoDispose(filteredRecords.addListener(() => {
    model.set(fromTableData(getTreeTableData(), buildDom, model.get()));
  }));

  // create a computed that reads the selected page from the url and return the corresponding item
  const selected = Computed.create(owner, activeDoc.activeViewId, (use, viewId) =>
    findInTree(model.get(), (i: TreeItemRecord) => i.record.viewRef === viewId) || null,
  );

  owner.autoDispose(createGroup({
    nextPage: () => selected.get() && otherPage(selected.get()!, +1),
    prevPage: () => selected.get() && otherPage(selected.get()!, -1),
  }, null, true));

  // dom
  return dom("nav",
    { "aria-label": t("Document pages") },
    cssPagesSearch(
      cssPagesSearchInput(
        { placeholder: t("Search pages") },
        dom.prop("value", use => use(searchValue)),
        dom.on("input", (ev) => searchValue.set((ev.target as HTMLInputElement).value)),
        testId("pages-search"),
      ),
    ),
    dom.maybe(use => use(hasSearch) && !use(hasResults), () =>
      cssPagesSearchEmpty("Нічого не знайдено"),
    ),
    dom.create(TreeViewComponent, model, { isOpen, selected, isReadonly: activeDoc.isReadonly }),
  );
}

const testId = makeTestId("test-removepage-");

function buildDomFromTable(
  pagesTable: MetaTableModel<PageRec>,
  activeDoc: GristDoc,
  pageId: number,
  item: TreeItemRecord,
) {
  if (item.hidden) {
    return buildCensoredPage();
  }

  const { isReadonly } = activeDoc;
  const canEditStructure = isRegionalStructureEditor(getRegionalCapabilitiesObs(activeDoc.app.topAppModel).get());
  const pageRec = pagesTable.rowModels[pageId];
  const viewRec = pageRec.view.peek();
  const pageName = viewRec.name;
  const viewId = viewRec.id.peek();
  const { docModel } = activeDoc;

  const options: PageOptions = {
    onRename: async (newName: string) => {
      if (newName.length) {
        await pageName.saveOnly(newName);
      }
    },
    onRemove: () => removeView(activeDoc, viewId, pageName.peek()),
    onDuplicate: () => buildDuplicatePageDialog(activeDoc, pageId),
    // Can't remove last visible page
    isRemoveDisabled: () => docModel.visibleDocPages.peek().length <= 1,
    isReadonly,
    isCollapsed: pageRec.isCollapsed,
    onCollapse: value => pageRec.isCollapsed.set(value),
    isCollapsedByDefault: pageRec.isCollapsedByDefault,
    onCollapseByDefault: value => pageRec.setAndSaveCollapsed(value),
    hasSubPages: () => item.children().get().length > 0,
    href: urlState().setLinkUrl({ docPage: viewId }),
    canRename: canEditStructure,
    canManage: canEditStructure,
  };

  return buildPageDom(fromKo(pageName), options);
}

function removeView(activeDoc: GristDoc, viewId: number, pageName: string) {
  logTelemetryEvent("deletedPage", { full: { docIdDigest: activeDoc.docId() } });

  const docData = activeDoc.docData;
  // Create a set with tables on other pages (but not on this one).
  const tablesOnOtherViews = new Set(activeDoc.docModel.viewSections.rowModels
    .filter(vs => !vs.isRaw.peek() && !vs.isRecordCard.peek() && vs.parentId.peek() !== viewId)
    .map(vs => vs.tableRef.peek()));

  // Check if this page is a last page for some tables.
  const notVisibleTables = [...new Set(activeDoc.docModel.viewSections.rowModels
    .filter(vs => vs.parentId.peek() === viewId) // Get all sections on this view
    .filter(vs => !vs.table.peek().summarySourceTable.peek()) // Sections that have normal tables
    .filter(vs => !tablesOnOtherViews.has(vs.tableRef.peek())) // That aren't on other views
    .filter(vs => vs.table.peek().tableId.peek()) // Which we can access (has tableId)
    .map(vs => vs.table.peek()))]; // Return tableRec object, and remove duplicates.

  const removePage = () => [["RemoveRecord", "_grist_Views", viewId]];
  const removeAll = () => [
    ...removePage(),
    ...notVisibleTables.map(tb => ["RemoveTable", tb.tableId.peek()]),
  ];

  if (notVisibleTables.length) {
    const tableNames = notVisibleTables.map(tb => tb.tableNameDef.peek());
    buildPrompt(tableNames, async (option) => {
      // Errors are handled in the dialog.
      if (option === "data") {
        await docData.sendActions(removeAll(), `Remove page ${pageName} with tables ${tableNames}`);
      } else if (option === "page") {
        await docData.sendActions(removePage(), `Remove only page ${pageName}`);
      } else {
        // This should not happen, as save should be disabled when no option is selected.
      }
    });
  } else {
    return docData.sendActions(removePage(), `Remove only page ${pageName}`);
  }
}

type RemoveOption = "" | "data" | "page";

// Select another page in cyclic ordering of pages. Order is downard if given a positive `delta`,
// upward otherwise.
function otherPage(currentPage: TreeItemRecord, delta: number) {
  const records = currentPage.storage.records;
  const index = mod(currentPage.index + delta, records.length);
  const docPage = records[index].viewRef;
  return urlState().pushUrl({ docPage });
}

function buildPrompt(tableNames: string[], onSave: (option: RemoveOption) => Promise<any>) {
  saveModal((ctl, owner): ISaveModalOptions => {
    const selected = Observable.create<RemoveOption>(owner, "");
    const saveDisabled = Computed.create(owner, use => use(selected) === "");
    const saveFunc = () => onSave(selected.get());
    return {
      title: t("The following tables will no longer be visible", { count: tableNames.length }),
      body: dom("div",
        testId("popup"),
        buildWarning(tableNames),
        cssRadioCheckboxOptions(
          radioCheckboxOption(selected, "data", t("Delete data and this page.")),
          radioCheckboxOption(selected, "page",
            t("Keep data and delete page. Table will remain available in {{rawDataLink}}",
              {
                rawDataLink: cssLink(
                  t("raw data page"),
                  urlState().setHref({ docPage: "data" }),
                  { target: "_blank" },
                ),
              },
            ),
          ),
        ),
      ),
      saveDisabled,
      saveLabel: t("Delete"),
      saveFunc,
      width: "fixed-wide",
      extraButtons: [],
    };
  });
}

function buildWarning(tables: string[]) {
  return cssWarning(
    dom.forEach(tables, tb => cssTableName(tb, testId("table"))),
  );
}

const cssWarning = styled("div", `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`);

const cssTableName = styled("div", `
  color: ${theme.choiceTokenFg};
  background-color: ${theme.choiceTokenBg};
  padding: 3px 6px;
  border-radius: 4px;
`);

function normalizePageSearchText(value: string): string {
  return value.toLocaleLowerCase().replace(/\s+/g, " ").trim();
}

function filterPageRecords(records: TreeRecord[], searchText: string): TreeRecord[] {
  const includedIds = new Set<number>();
  const stack: TreeRecord[] = [];
  const matchedAncestorIndents: number[] = [];
  for (const record of records) {
    while (stack.length > record.indentation) {
      stack.pop();
    }
    while (matchedAncestorIndents.length && matchedAncestorIndents[matchedAncestorIndents.length - 1] >= record.indentation) {
      matchedAncestorIndents.pop();
    }
    const haystack = normalizePageSearchText(String((record as any).pageName || ""));
    const matches = haystack.includes(searchText);
    if (matches || matchedAncestorIndents.length > 0) {
      includedIds.add(record.id);
    }
    if (matches) {
      for (const ancestor of stack) {
        includedIds.add(ancestor.id);
      }
      matchedAncestorIndents.push(record.indentation);
    }
    stack[record.indentation] = record;
    stack.length = record.indentation + 1;
  }
  return records
    .filter(record => includedIds.has(record.id))
    .map(record => ({ ...record, collapsed: observable(false) }));
}

const cssPagesSearch = styled("div", `
  margin: 0 12px 8px 12px;
`);

const cssPagesSearchInput = styled("input", `
  width: 100%;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${theme.inputBorder};
  background: ${theme.inputBg};
  color: ${theme.inputFg};
  padding: 0 10px;
  outline: none;

  &::placeholder {
    color: ${theme.lightText};
  }

  &:focus {
    border-color: ${theme.controlFg};
  }
`);

const cssPagesSearchEmpty = styled("div", `
  margin: 0 12px 8px 12px;
  color: ${theme.lightText};
  font-size: 12px;
`);
